import { i18n } from "@/i18n";
import { questionnaireFieldTypeConstante } from "@/constantes/questionnaire-field-type";
import { formManager } from "@/services/form/form-manager";
import { questionnaireStore, studeaManagerQuestionnaireStore } from "@/main";
import { useQuestionnaireStore } from "@/store/questionnaire/questionnaire";

class QuestionnaireModelBuilder {
  getFieldBlockPrefixes(type) {
    let block_prefixes = [];
    switch (type) {
      case 0:
        block_prefixes = ["", "text"];
        break;
      case 1:
        block_prefixes = ["", "text", "textarea", "zama_tiny_mce_type"];
        break;
      case 2:
      case 3:
      case 4:
        block_prefixes = ["", "choice"];
        break;
      case 8:
        block_prefixes = ["", "number"];
        break;
      case 10:
        block_prefixes = ["", "file"];
        break;
      case 11:
        block_prefixes = ["", "date"];
        break;
      case 12:
        block_prefixes = ["", "date", "datetime"];
        break;
    }

    return block_prefixes;
  }

  buildModel(questionnaire) {
    let model = {};
    if (questionnaire && questionnaire.questions && Object.keys(questionnaire.questions).length) {
      Object.values(questionnaire.questions).forEach((question) => {
        if (question.itemType === "field") {
          model[question.id] = this.buildField(questionnaire, question);
        } else if (question.itemType === "cluster" && question.displayMode === 2) {
          if (question.questions && Object.keys(question.questions).length) {
            Object.values(question.questions).forEach((row) => {
              row["cluster"] = question.id;
              const name =
                question.userAnswer && question.userAnswer.arrayValue && question.userAnswer.arrayValue.name
                  ? question.userAnswer.arrayValue.name
                  : null;
              const comments =
                row.userAnswer && row.userAnswer.arrayValue && row.userAnswer.arrayValue.comments
                  ? row.userAnswer.arrayValue.comments
                  : null;
              let values =
                row.userAnswer && row.userAnswer.arrayValue && row.userAnswer.arrayValue.answers
                  ? row.userAnswer.arrayValue.answers
                  : null;

              if (name) {
                row["name"] = name;
              }
              model[row.id] = this.buildField(questionnaire, row, question.type);
              model[row.id].vars["row"] = true;

              if (comments) {
                model[row.id].vars["comments"] = comments;
              }

              if (!values) {
                values = [];
                if (question.valueList) {
                  for (let i = 0; i < question.valueList.length; i++) {
                    values[i] = "";
                  }
                }
              }
              model[row.id].vars.value = values;
            });
          }
          if (question.openedQuestions) {
            if (question.userAnswerCollections && Object.keys(question.userAnswerCollections).length) {
              Object.values(question.userAnswerCollections).forEach((userAnswerCollection) => {
                if (Object.keys(userAnswerCollection.answersValues).length) {
                  Object.values(userAnswerCollection.answersValues).forEach((row) => {
                    this.addClusterLine(row, question);
                    model[row.id] = this.buildField(questionnaire, row, question.type, question);
                    model[row.id]["cluster"] = question.id;
                    if (row.arrayValue) {
                      model[row.id]["name"] = row.arrayValue.name ? row.arrayValue.name : null;
                      if (row.arrayValue.comments) {
                        model[row.id].vars["comments"] = row.arrayValue.comments;
                      }
                      row["answer"] = row.arrayValue.answers;
                      model[row.id].vars.value = row.answer;
                    }
                  });
                }
              });
            }
          }
        }
      });
    }

    return model;
  }

  buildField(questionnaire, question, type = null, cluster = null, value = null) {
    let comments;
    type = type ? type : question.type;
    if (question.userAnswer && question.userAnswer.arrayValue && question.userAnswer.arrayValue.answers && !value) {
      value = this.isValidAnswerForFieldType(type, question.userAnswer.arrayValue.answers)
        ? question.userAnswer.arrayValue.answers
        : null;
      comments =
        typeof question.userAnswer.arrayValue.comments === "object"
          ? question.userAnswer.arrayValue.comments.value
          : question.userAnswer.arrayValue.comments;
    }

    const disabled = this.disabled(question);

    const field = {
      vars: {
        value: value,
        block_prefixes: this.getFieldBlockPrefixes(type),
        required: question.required,
        id: question.id,
        name: "name-" + question.id,
        label: question.title,
        help: this.help(question),
        disabled: disabled,
        ignoreValidation: disabled,
        hide: !question.enabled || (questionnaire && questionnaire.userHasSigned),
        attr: {},
        comments: comments,
      },
    };

    if (question.type === questionnaireFieldTypeConstante.QUESTION_TYPE_FILE || type === 1) {
      field.vars.max_file_size = 10;
      field.vars["conf_name"] = "triad";
      field.vars["conf_path"] = "questionnaire";
      field.vars["extensions_accepted_message"] = i18n.global.t("extensions_accepted_document_image");
      field.vars["extensions_accepted"] = "(\\.|\\/)(gif|jpe?g|png|pdf|doc|docx|xlsx|xls|pptx)$";
    }

    if (this.isChoice(type)) {
      switch (type) {
        case questionnaireFieldTypeConstante.QUESTION_TYPE_SELECT:
          field.vars["multiple"] = false;
          field.vars["expanded"] = false;
          break;
        case questionnaireFieldTypeConstante.QUESTION_TYPE_RADIO:
          field.vars["multiple"] = false;
          field.vars["expanded"] = true;
          break;
        case questionnaireFieldTypeConstante.QUESTION_TYPE_CHECKBOX:
          field.vars["multiple"] = true;
          field.vars["expanded"] = true;
          break;
      }

      let choices = [];
      (cluster ? cluster : question).valueList.forEach((choice) => {
        let isChecked = false;

        // Determine if this choice is checked based on the current value
        if (value) {
          if (Array.isArray(value)) {
            // For checkbox/radio with array values
            isChecked = !!value.find((item) => {
              return (item && typeof item === "object" && item.value === choice.value) || item === choice.value;
            });
          } else if (typeof value === "object") {
            // For object values, check if any value matches
            isChecked = !!Object.values(value).find((item) => item === choice.value);
          } else if (typeof value === "string") {
            // For string values (select), check if it matches exactly
            isChecked = String(value) === String(choice.value);
          }
        }

        choices.push({
          label: choice.value,
          value: choice.value,
          data: choice.value,
          mark: choice.mark ? choice.mark : null,
          checked: isChecked,
        });
      });
      field.vars.choices = choices;
      if (this.isCheckbox(field)) {
        field.vars.value = choices;
      } else if (type === questionnaireFieldTypeConstante.QUESTION_TYPE_SELECT && typeof value === "string") {
        field.vars.value = choices.find((choice) => String(choice.value) === String(value)) || null;
      }
    }

    return field;
  }

  isValidAnswerForFieldType(type, answer) {
    if (answer === null || answer === undefined) return false;
    const isDate = /^\d{4}-\d{2}-\d{2}$/.test(answer);
    const isDateTime = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(answer);

    if (
      [
        questionnaireFieldTypeConstante.QUESTION_TYPE_TEXT,
        questionnaireFieldTypeConstante.QUESTION_TYPE_TEXTAREA,
        questionnaireFieldTypeConstante.QUESTION_TYPE_RADIO,
      ].includes(type) &&
      !isDate &&
      !isDateTime
    ) {
      return typeof answer === "string";
    }

    if (type === questionnaireFieldTypeConstante.QUESTION_TYPE_SELECT)
      return typeof answer === "object" || typeof answer === "string";
    if (type === questionnaireFieldTypeConstante.QUESTION_TYPE_CHECKBOX) return Array.isArray(answer);
    if (type === questionnaireFieldTypeConstante.QUESTION_TYPE_NUMBER) return typeof answer === "number";
    if (type === questionnaireFieldTypeConstante.QUESTION_TYPE_FILE)
      return (
        typeof answer === "object" &&
        answer.pathName !== undefined &&
        (answer.id !== undefined || answer.fromMigrationV2 === true)
      );
    if (type === questionnaireFieldTypeConstante.QUESTION_TYPE_DATE) return isDate;
    if (type === questionnaireFieldTypeConstante.QUESTION_TYPE_DATETIME) return isDateTime;
    return false;
  }

  addClusterLine(answer, cluster) {
    if (!cluster.questions) {
      cluster.questions = [];
    }

    const exist = Object.values(cluster.questions).find((item) => item.id === answer.id);

    if (!exist) {
      studeaManagerQuestionnaireStore.initLastIndex(studeaManagerQuestionnaireStore.lastIndex + 1);

      const item = {
        id: answer.id,
        index: studeaManagerQuestionnaireStore.lastIndex,
        title: answer.arrayValue && answer.arrayValue.name ? answer.arrayValue.name : "",
        type: cluster.type,
        clusterType: "field",
        access: cluster.openedQuestionsAccess,
        comments: cluster.comments,
        commentsLabel: cluster.commentsLabel,
        commentsType: cluster.commentsType,
        openedQuestionsAccess: [],
        openedQuestions: false,
        openedQuestionsType: 1,
        enabled: this.isEnabled(cluster),
      };

      cluster.questions.push(item);
    }
  }

  isEnabled(question) {
    const questionnaire = useQuestionnaireStore().questionnaire;
    return (
      question.openedQuestionsAccess.includes(questionnaire.userProfile) &&
      questionnaire.userHasToComplete &&
      !questionnaire.hasSigned
    );
  }

  isChoice(type) {
    return [
      questionnaireFieldTypeConstante.QUESTION_TYPE_SELECT,
      questionnaireFieldTypeConstante.QUESTION_TYPE_RADIO,
      questionnaireFieldTypeConstante.QUESTION_TYPE_CHECKBOX,
    ].includes(type);
  }

  isNumber(type) {
    return type === questionnaireFieldTypeConstante.QUESTION_TYPE_NUMBER;
  }

  isMission(type) {
    return type === questionnaireFieldTypeConstante.QUESTION_TYPE_MISSION;
  }

  isActivity(type) {
    return type === questionnaireFieldTypeConstante.QUESTION_TYPE_ACTIVITY;
  }

  isMarkVisible(type) {
    return [
      questionnaireFieldTypeConstante.QUESTION_TYPE_RADIO,
      questionnaireFieldTypeConstante.QUESTION_TYPE_SELECT,
      questionnaireFieldTypeConstante.QUESTION_TYPE_NUMBER,
    ].includes(type);
  }

  hasOtherQuestionOfType(questions, currentIndex, type) {
    return Object.values(questions || {}).some((question) => {
      const isOther = currentIndex === undefined || question.index !== currentIndex;
      if (isOther && question.type === type) {
        return true;
      }
      return this.hasOtherQuestionOfType(question.questions, currentIndex, type);
    });
  }

  help(question) {
    return question.tipActive && question.tip ? question.tip : null;
  }

  disabled(question) {
    let available =
      questionnaireStore.questionnaire.userHasToComplete &&
      !questionnaireStore.questionnaire.userHasSigned &&
      question.enabled;

    return !available;
  }

  isCheckbox(question) {
    return formManager.isCheckboxType(question);
  }

  getColumnMaxWidth(column) {
    if (column.value && column.value.length) {
      return 40 + 4 * column.value.length;
    } else {
      return 60;
    }
  }
}

export const questionnaireModelBuilder = new QuestionnaireModelBuilder();
